<template>
  <div
    class="tooltip-wrapper"
    ref="trigger"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
  >
    <slot />
    <!-- Table wrapper mode: teleport and fixed positioning -->
    <teleport
      to="body"
      v-if="tableWrapperMode"
    >
      <span
        v-if="show"
        :class="tooltipClasses"
        :style="tooltipStyle"
        @mouseenter="onEnter"
        @mouseleave="onLeave"
      >
        <slot name="content">
          <span
            v-for="(line, lineIndex) in formattedLines"
            :key="`tooltip-line-${lineIndex}`"
            class="tooltip-line"
          >
            <template
              v-for="(segment, segmentIndex) in line"
              :key="`tooltip-segment-${lineIndex}-${segmentIndex}`"
            >
              <span :class="{ 'tooltip-bold': segment.isBold }">{{
                segment.text
              }}</span>
            </template>
          </span>
        </slot>
      </span>
    </teleport>
    <!-- Normal mode: CSS-based tooltip -->
    <span
      v-else
      :class="tooltipClasses"
      :style="tooltipStyle"
    >
      <slot name="content">{{ text }}</slot>
    </span>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  text: { type: String, default: '', required: false },
  position: { type: String, default: 'bottom' },
  tableWrapperMode: { type: Boolean, default: false },
  width: { type: [String, Number], default: null },
  maxWidth: { type: [String, Number], default: null },
})

const show = ref(false)
const trigger = ref(null)

function getFixedTooltipPosition() {
  const element = trigger.value
  if (!element || typeof element.getBoundingClientRect !== 'function') {
    return {
      top: '50vh',
      left: '75vw',
      transform: 'translate(-50%, -50%)',
    }
  }

  const rect = element.getBoundingClientRect()
  const viewportWidth =
    window.innerWidth || document.documentElement.clientWidth || 0
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight || 0
  const preferredWidth = 520
  const horizontalPadding = 16
  const verticalPadding = 12
  const estimatedTooltipHeight = 88

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

  const centeredTopPx = clamp(
    rect.top + rect.height / 2,
    verticalPadding + estimatedTooltipHeight / 2,
    Math.max(
      verticalPadding + estimatedTooltipHeight / 2,
      viewportHeight - verticalPadding - estimatedTooltipHeight / 2,
    ),
  )

  const leftPx = Math.min(
    Math.max(
      rect.left + rect.width / 2,
      horizontalPadding + preferredWidth / 2,
    ),
    Math.max(
      horizontalPadding + preferredWidth / 2,
      viewportWidth - horizontalPadding - preferredWidth / 2,
    ),
  )

  if (props.position === 'left') {
    return {
      top: `${centeredTopPx}px`,
      left: `${Math.max(rect.left - 8, horizontalPadding)}px`,
      transform: 'translate(-100%, -50%)',
    }
  }

  if (props.position === 'right') {
    return {
      top: `${centeredTopPx}px`,
      left: `${Math.min(rect.right + 8, viewportWidth - horizontalPadding)}px`,
      transform: 'translateY(-50%)',
    }
  }

  const prefersTop = props.position === 'top'
  const canShowBelow =
    viewportHeight - rect.bottom >= estimatedTooltipHeight + verticalPadding
  const canShowAbove = rect.top >= estimatedTooltipHeight + verticalPadding

  if (prefersTop) {
    if (canShowAbove || !canShowBelow) {
      return {
        top: `${Math.max(rect.top - 8, verticalPadding)}px`,
        left: `${leftPx}px`,
        transform: 'translate(-50%, -100%)',
      }
    }
  } else if (!canShowBelow && canShowAbove) {
    return {
      top: `${Math.max(rect.top - 8, verticalPadding)}px`,
      left: `${leftPx}px`,
      transform: 'translate(-50%, -100%)',
    }
  }

  return {
    top: `${rect.bottom + 8}px`,
    left: `${leftPx}px`,
    transform: 'translateX(-50%)',
  }
}

function onEnter() {
  if (props.tableWrapperMode) {
    show.value = true
  }
}
function onLeave() {
  if (props.tableWrapperMode) {
    show.value = false
  }
}

const tooltipClasses = computed(() => {
  if (props.tableWrapperMode) {
    return ['tooltip-fixed']
  }
  return ['tooltip__text', 'tooltip-abs', `tooltip--${props.position}`]
})

const tooltipStyle = computed(() => {
  const isShown = show.value
  const resolvedWidth =
    typeof props.width === 'number' ? `${props.width}px` : props.width || null
  const resolvedMaxWidth =
    typeof props.maxWidth === 'number'
      ? `${props.maxWidth}px`
      : props.maxWidth || null

  if (props.tableWrapperMode) {
    const fixedPosition = isShown
      ? getFixedTooltipPosition()
      : getFixedTooltipPosition()

    return {
      position: 'fixed',
      top: fixedPosition.top,
      left: fixedPosition.left,
      transform: fixedPosition.transform,
      zIndex: 999999,
      ...(resolvedWidth ? { width: resolvedWidth } : {}),
      ...(resolvedMaxWidth ? { maxWidth: resolvedMaxWidth } : {}),
    }
  }

  return {
    ...(resolvedWidth ? { width: resolvedWidth } : {}),
    ...(resolvedMaxWidth ? { maxWidth: resolvedMaxWidth } : {}),
  }
})

function formatTooltipLine(rawLine) {
  const segments = []
  const line = rawLine || ''
  const marker = 'ITEM BROKEN'
  let startIndex = 0

  while (startIndex < line.length) {
    const markerIndex = line.indexOf(marker, startIndex)

    if (markerIndex === -1) {
      segments.push({ text: line.slice(startIndex), isBold: false })
      break
    }

    if (markerIndex > startIndex) {
      segments.push({
        text: line.slice(startIndex, markerIndex),
        isBold: false,
      })
    }

    segments.push({ text: marker, isBold: true })
    startIndex = markerIndex + marker.length
  }

  if (segments.length === 0) {
    segments.push({ text: '', isBold: false })
  }

  return segments
}

const formattedLines = computed(() => {
  const raw = props.text || ''
  return raw.split('\n').map((line) => formatTooltipLine(line))
})
</script>

<style>
.tooltip__text {
  z-index: 9999;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s ease;
  transition-delay: 0.25s;
  font-size: clamp(1.1rem, 1.15vw, 1.35rem);
  line-height: 1.35;
  color: white;
  text-align: center;
  padding: 12px 16px;
  border-radius: 5px;
  min-width: 150px;
  max-width: 480px;
  background: orange;
  pointer-events: none;
  white-space: pre-wrap;
  word-break: break-word;
  box-sizing: border-box;
  overflow-wrap: break-word;
  min-height: fit-content;
  display: block;
  position: absolute;
  /* positioned relative to wrapper */
}

/* Hidden by default for absolute tooltip */
/* .tooltip-abs {} */

/* Fixed/teleport tooltip */
.tooltip-fixed {
  white-space: pre-line;
  position: fixed;
  visibility: visible;
  opacity: 1;
  width: auto;
  min-width: 150px;
  max-width: 480px;
  box-sizing: border-box;
  word-break: break-word;
  overflow-wrap: break-word;
  overflow: visible;
  color: white;
  background: orange;
  padding: 12px 16px;
  font-size: clamp(1.1rem, 1.15vw, 1.35rem);
  line-height: 1.35;
  text-align: center;
  border-radius: 5px;
  z-index: 999999;
  display: block;
  min-height: fit-content;
}

.tooltip-line {
  display: block;
}

.tooltip-bold {
  font-weight: bold;
}

.tooltip__text img {
  max-width: 440px;
  height: auto;
  display: block;
  margin: 10px auto;
  margin-right: 30px;
  padding: 10px;
}

.tooltip-wrapper {
  display: inline-block;
  position: relative;
}

/* show tooltip on hover (non-table mode) */
.tooltip-wrapper:hover .tooltip__text {
  visibility: visible;
  opacity: 1;
}

/* ----------------------------
   POSITIONING: keep arrow centered
   ---------------------------- */

/* TOP: tooltip above, horizontally centered */
.tooltip--top {
  left: 50%;
  bottom: 100%;
  transform: translateX(-50%) translateY(-8px);
  margin: 0;
}

/* BOTTOM: tooltip below, horizontally centered */
.tooltip--bottom {
  left: 50%;
  top: 100%;
  transform: translateX(-50%) translateY(8px);
  margin: 0;
}

/* LEFT: tooltip to left of target, vertically centered */
.tooltip--left {
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-right: 8px;
}

/* RIGHT: tooltip to right of target, vertically centered */
.tooltip--right {
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 8px;
}

/* ----------------------------
   ARROW: use pseudo-element centered vertically
   ---------------------------- */
.tooltip__text::after {
  content: '';
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
}

/* Arrow for left: placed at right edge, vertically centered */
.tooltip__text.tooltip--left::after {
  top: 50%;
  right: -7px;
  /* half of border-width */
  transform: translateY(-50%);
  border-width: 7px 0 7px 7px;
  border-color: transparent transparent transparent orange;
}

/* Arrow for right: placed at left edge, vertically centered */
.tooltip__text.tooltip--right::after {
  top: 50%;
  left: -7px;
  transform: translateY(-50%);
  border-width: 7px 7px 7px 0;
  border-color: transparent orange transparent transparent;
}

/* Arrow for top: placed at bottom center of tooltip */
.tooltip__text.tooltip--top::after {
  left: 50%;
  bottom: -7px;
  transform: translateX(-50%);
  border-width: 7px 7px 0 7px;
  border-color: orange transparent transparent transparent;
}

/* Arrow for bottom: placed at top center of tooltip */
.tooltip__text.tooltip--bottom::after {
  left: 50%;
  top: -7px;
  transform: translateX(-50%);
  border-width: 0 7px 7px 7px;
  border-color: transparent transparent orange transparent;
}

/* Accessibility: show tooltip when focused via keyboard */
:slotted(*):focus + .tooltip__text {
  visibility: visible;
  opacity: 1;
}
</style>
